/**
 * DERAR2Service - Cliente para Worker DERA R2
 *
 * Gestiona la carga lazy de datos DERA municipales desde el Worker
 * con cache en memoria para evitar refetch innecesarios.
 *
 * @module services/dera
 * @version 1.0.0
 */

import type { DERAR2Feature, DERATipologia } from '../../types/deraR2';
import { PROVINCIAS_ANDALUCIA } from '../../types/catastro';
import { WORKER_BASE_V1 } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local, ruta /v1).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE_V1`).
 */
const WORKER_BASE_URL = WORKER_BASE_V1;
const CACHE_MAX_ITEMS = 50;
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutos

// =============================================================================
// TIPOS INTERNOS
// =============================================================================

interface CacheEntry {
  data: DERAR2Feature[];
  timestamp: number;
}

// =============================================================================
// SINGLETON SERVICE
// =============================================================================

let serviceInstance: DERAR2Service | null = null;

export function getDERAR2Service(): DERAR2Service {
  if (!serviceInstance) {
    serviceInstance = new DERAR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetDERAR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class DERAR2Service {
  private readonly baseUrl: string;
  private readonly cache: Map<string, CacheEntry> = new Map();
  private readonly maxCacheSize: number;
  private readonly cacheTTL: number;
  private loadingPromises: Map<string, Promise<DERAR2Feature[]>> = new Map();

  /** Caché global de todos los datos DERA (all-dera endpoint) */
  private allDeraCache: { data: DERAR2Feature[]; ts: number } | null = null;
  private loadingAllDera: Promise<DERAR2Feature[]> | null = null;

  constructor(baseUrl?: string, maxCacheSize = CACHE_MAX_ITEMS, cacheTTL = CACHE_TTL_MS) {
    this.baseUrl = baseUrl || WORKER_BASE_URL;
    this.maxCacheSize = maxCacheSize;
    this.cacheTTL = cacheTTL;
  }

  /** Carga infraestructuras DERA de un municipio */
  async loadMunicipio(codINE: string): Promise<DERAR2Feature[]> {
    if (!/^\d{5}$/.test(codINE)) {
      console.warn(`[DERAR2] Código INE inválido: ${codINE}`);
      return [];
    }

    const provincia = codINE.substring(0, 2);
    if (!PROVINCIAS_ANDALUCIA.includes(provincia as (typeof PROVINCIAS_ANDALUCIA)[number])) {
      console.warn(`[DERAR2] Provincia no andaluza: ${provincia}`);
      return [];
    }

    const cached = this.getFromCache(codINE);
    if (cached) return cached;

    const existingPromise = this.loadingPromises.get(codINE);
    if (existingPromise) return existingPromise;

    const loadPromise = this.fetchMunicipio(codINE);
    this.loadingPromises.set(codINE, loadPromise);

    try {
      const features = await loadPromise;
      this.setInCache(codINE, features);
      return features;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  private async fetchMunicipio(codINE: string): Promise<DERAR2Feature[]> {
    // DERA R2 no tiene endpoint por municipio — endpoint real: /v1/dera/{tipo}
    // Cargamos all-dera globalmente (una sola petición, caché compartida)
    // y filtramos en memoria por codMun === codINE.
    const all = await this.loadAllDera();
    const muniFeatures = all.filter(f => f.cod_ine === codINE);
    console.log(`[DERAR2] Cargado ${codINE}: ${muniFeatures.length} infraestructuras`);
    return muniFeatures;
  }

  /**
   * Carga todos los datos DERA desde el endpoint all-dera del Worker.
   * Resultado cacheado globalmente; llamadas concurrentes se deducan.
   */
  private async loadAllDera(): Promise<DERAR2Feature[]> {
    if (this.allDeraCache) return this.allDeraCache.data;
    if (this.loadingAllDera) return this.loadingAllDera;

    this.loadingAllDera = this.fetchAllDera();
    try {
      const data = await this.loadingAllDera;
      this.allDeraCache = { data, ts: Date.now() };
      return data;
    } finally {
      this.loadingAllDera = null;
    }
  }

  private async fetchAllDera(): Promise<DERAR2Feature[]> {
    const url = `${this.baseUrl}/dera/all-dera`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const raw = await response.json() as Record<string, unknown>;
      // Worker devuelve DERAFeatureCollection: { type, features:[...], metadata }
      // o array directo (retrocompatible)
      const rawFeatures = Array.isArray(raw)
        ? raw
        : ((raw?.features as unknown[]) ?? []);
      return (rawFeatures as Record<string, unknown>[]).map(f => this.normalizeWorkerFeature(f));
    } catch (error) {
      console.error('[DERAR2] Error cargando all-dera:', error);
      return [];
    }
  }

  /**
   * Normaliza una feature del Worker al tipo interno DERAR2Feature.
   * Worker usa `codMun` / `codProv`; el tipo interno usa `cod_ine` / `cod_prov`.
   */
  private normalizeWorkerFeature(raw: Record<string, unknown>): DERAR2Feature {
    return {
      id: String(raw.id ?? ''),
      nombre: String(raw.nombre ?? ''),
      tipologia: (raw.tipologia) as DERATipologia,
      subtipo: String(raw.subtipo ?? ''),
      x: Number(raw.x ?? 0),
      y: Number(raw.y ?? 0),
      municipio: String(raw.municipio ?? ''),
      cod_ine: String(raw.codMun ?? raw.cod_ine ?? ''),
      provincia: String(raw.provincia ?? ''),
      cod_prov: String(raw.codProv ?? raw.cod_prov ?? ''),
      direccion: raw.direccion != null ? String(raw.direccion) : undefined,
      fuente: String(raw.fuente ?? 'DERA R2'),
    };
  }

  private getFromCache(codINE: string): DERAR2Feature[] | null {
    const entry = this.cache.get(codINE);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(codINE);
      return null;
    }

    return entry.data;
  }

  private setInCache(codINE: string, data: DERAR2Feature[]): void {
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(codINE, {
      data,
      timestamp: Date.now(),
    });
  }

  // ===========================================================================
  // BÚSQUEDA ESPACIAL EN MEMORIA
  // ===========================================================================

  /**
   * Busca la infraestructura más próxima de un tipo dado operando sobre
   * las features ya cargadas en memoria (sin I/O adicional si el municipio
   * ya está en caché). Útil para obtener el nombre canónico y corregir
   * preposiciones (de/del/de la) en el texto de dirección.
   *
   * @param x         Coordenada X EPSG:25830
   * @param y         Coordenada Y EPSG:25830
   * @param tipologia Tipología DERA a buscar
   * @param codINE    Código INE del municipio (5 dígitos)
   * @param radioM    Radio de búsqueda en metros (defecto 150)
   * @returns Feature más cercana dentro del radio, o null si no hay ninguna
   */
  async findNearestByType(
    x: number,
    y: number,
    tipologia: DERATipologia,
    codINE: string,
    radioM = 150
  ): Promise<DERAR2Feature | null> {
    const features = await this.loadMunicipio(codINE);
    return this.findNearestInMemory(features, x, y, tipologia, radioM);
  }

  /**
   * Búsqueda espacial pura en memoria (sin I/O).
   * Expuesta como método público para testabilidad directa sin mocks de red.
   *
   * Utiliza distancia euclídea sobre EPSG:25830 (metros).
   * Se incluye la feature cuya distancia es exactamente igual al radio.
   *
   * @param features  Array de features DERA ya cargadas
   * @param x         Coordenada X EPSG:25830
   * @param y         Coordenada Y EPSG:25830
   * @param tipologia Tipología DERA a filtrar
   * @param radioM    Radio de búsqueda en metros (defecto 150)
   * @returns Feature más cercana dentro del radio, o null
   */
  findNearestInMemory(
    features: DERAR2Feature[],
    x: number,
    y: number,
    tipologia: DERATipologia,
    radioM = 150
  ): DERAR2Feature | null {
    let nearest: DERAR2Feature | null = null;
    let minDist = Infinity;

    for (const f of features) {
      if (f.tipologia !== tipologia) continue;
      const dx = f.x - x;
      const dy = f.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= radioM && dist < minDist) {
        minDist = dist;
        nearest = f;
      }
    }

    return nearest;
  }

  /** Limpia la cache */
  clearCache(): void {
    this.cache.clear();
    this.allDeraCache = null;
    this.loadingAllDera = null;
  }

  /** Estadísticas de cache */
  getCacheStats(): { size: number; maxSize: number } {
    return { size: this.cache.size, maxSize: this.maxCacheSize };
  }

  /** Verifica si un municipio está en cache */
  hasMunicipio(codINE: string): boolean {
    return this.getFromCache(codINE) !== null;
  }
}
